/** Reports → Metrics: visit mix, clients and status of work for the report's range and filters. */
import { useMemo, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { T } from "./tokens";
import { Panel, ShowAllButton } from "./ui";
import { useActiveConsultants, useTeamFilter, useTeamOf, useVisitsByConsultant } from "./data";
import type { SfVisit } from "./data";
import { classify, VISIT_TYPE_LABEL } from "./points";
import type { ReportRange } from "./reportRange";

const fmtMins = (m?: number | null) => {
    if (m == null || !isFinite(m) || m <= 0) return "—";
    const h = Math.floor(m / 60);
    return h > 0 ? `${h}h ${String(Math.round(m % 60)).padStart(2, "0")}m` : `${Math.round(m)}m`;
};

const median = (xs: number[]) => {
    if (xs.length === 0) return null;
    const a = [...xs].sort((x, y) => x - y);
    return a[Math.floor(a.length / 2)];
};

const Stat = ({ value, label, tip, color }: { value: string | number; label: string; tip?: string; color?: string }) => (
    <Box title={tip} sx={{ background: T.rowTint, border: `1px solid ${T.borderSoft}`, borderRadius: "8px", padding: "10px 11px" }}>
        <Typography sx={{ fontSize: "18px", fontWeight: 800, color: color || T.ink2, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
        <Typography sx={{ fontSize: "10.5px", color: T.muted, lineHeight: 1.3 }}>{label}</Typography>
    </Box>
);

const BarRow = ({ label, value, max, color }: { label: string; value: number; max: number; color?: string }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", padding: "4px 0" }}>
        <Typography sx={{ fontSize: "11.5px", color: T.muted, width: "150px", flexShrink: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</Typography>
        <Box sx={{ flexGrow: 1, height: "9px", borderRadius: "5px", background: T.rowTint, overflow: "hidden" }}>
            <Box sx={{ width: `${max > 0 ? (value / max) * 100 : 0}%`, height: "100%", background: color || T.purple }} />
        </Box>
        <Typography sx={{ fontSize: "11.5px", fontWeight: 800, color: T.ink2, width: "50px", textAlign: "right", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
    </Box>
);

const MetricsTab = ({ range, team = "AllTeams", consultant = "AllConsultants", onOpen }: { range: ReportRange; team?: string; consultant?: string; onOpen?: (email: string, name: string, teamName: string) => void }) => {
    const teamOf = useTeamOf();
    const [showAllMix, setShowAllMix] = useState(false);
    const roster = useActiveConsultants();
    const inTeam = useTeamFilter(team);
    const visits = useVisitsByConsultant(range.from, range.to);

    const people = useMemo(
        () =>
            roster.active
                .filter((c) => inTeam(c.email))
                .filter((c) => consultant === "AllConsultants" || (c.email || "").toLowerCase() === consultant.toLowerCase()),
        [roster.active, inTeam, consultant],
    );

    const agg = useMemo(() => {
        const perPerson = people.map((c) => {
            const list = (visits.byEmail.get((c.email || "").toLowerCase()) ?? []).filter((v: SfVisit) => v.visitStartDate);
            return {
                email: c.email,
                name: c.name,
                list,
                physical: list.filter((v) => v.visitCategory !== "Remote").length,
                remote: list.filter((v) => v.visitCategory === "Remote").length,
            };
        });
        const delivered = perPerson.flatMap((p) => p.list);
        const count = (key: (v: SfVisit) => string) => {
            const m = new Map<string, number>();
            delivered.forEach((v) => m.set(key(v), (m.get(key(v)) || 0) + 1));
            return [...m.entries()].sort((a, b) => b[1] - a[1]);
        };
        const travel = delivered
            .map((v) => {
                const t = [v.travelStartTime, v.travelEndTime, v.actualStartTime, v.actualEndTime].map((x) => (x ? new Date(x).getTime() : NaN));
                return t.every(isFinite) ? (t[1] - t[0] - (t[3] - t[2])) / 60000 : null;
            })
            .filter((m): m is number => m != null && m > 0);
        return {
            perPerson: perPerson.map((p) => ({ ...p, total: p.physical + p.remote })).sort((a, b) => b.total - a.total),
            visits: delivered.length,
            mix: count((v) => VISIT_TYPE_LABEL[classify(v.subActivity)]),
            industries: count((v) => v.industry || "not set").slice(0, 8),
            statuses: count((v) => v.status || "—"),
            onSite: delivered.filter((v) => v.visitCategory !== "Remote").length,
            remote: delivered.filter((v) => v.visitCategory === "Remote").length,
            medOnSite: median(delivered.map((v) => v.attendanceMins).filter((m): m is number => m != null && m > 0)),
            medTravel: median(travel),
            medWriteUp: median(delivered.map((v) => (v.writeUpTimeMinutes == null ? null : Number(v.writeUpTimeMinutes))).filter((m): m is number => m != null && m > 0)),
            travelSample: travel.length,
            noIndustry: delivered.filter((v) => !v.industry).length,
        };
    }, [people, visits.byEmail]);

    if (roster.isLoading || visits.isLoading)
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", color: T.muted, padding: "24px 0" }}>
                <CircularProgress size={16} />
                <Typography sx={{ fontSize: "12.5px" }}>Loading visits…</Typography>
            </Box>
        );
    if (visits.isError) return <Typography sx={{ fontSize: "12.5px", color: T.red }}>Couldn't load visits for this range.</Typography>;

    const maxMix = Math.max(1, ...agg.perPerson.map((r) => r.total));
    const shown = showAllMix ? agg.perPerson : agg.perPerson.slice(0, 10);
    const empty = <Typography sx={{ fontSize: "12.5px", color: T.muted }}>Nothing in this range.</Typography>;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "10px" }}>
                <Stat value={people.length} label="Consultants" />
                <Stat value={agg.visits} label="Visits in range" />
                <Stat value={fmtMins(agg.medOnSite)} label="On site · median visit" />
                <Stat value={fmtMins(agg.medTravel)} label="Travel · median round trip" tip={`From ${agg.travelSample} visits with all four times`} />
                <Stat value={fmtMins(agg.medWriteUp)} label="Write-up · median" />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "16px", alignItems: "stretch" }}>
                <Panel title="Visit mix by consultant" sub="physical vs remote in the range">
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {shown.map((r) => (
                            <Box
                                key={r.email}
                                onClick={onOpen ? () => onOpen(r.email, r.name, teamOf(r.email)) : undefined}
                                title={onOpen ? `Open ${r.name}` : undefined}
                                sx={{ display: "grid", gridTemplateColumns: "150px 1fr 110px", gap: "10px", alignItems: "center", ...(onOpen ? { cursor: "pointer", borderRadius: "5px", "&:hover": { background: T.rowTint } } : {}) }}
                            >
                                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</Typography>
                                <Box sx={{ display: "flex", height: "16px", borderRadius: "5px", overflow: "hidden", background: T.rowTint, border: `1px solid ${T.borderSoft}` }}>
                                    <Box title={`${r.physical} physical`} sx={{ width: `${(r.physical / maxMix) * 100}%`, background: T.purpleDeep }} />
                                    <Box title={`${r.remote} remote`} sx={{ width: `${(r.remote / maxMix) * 100}%`, background: T.purple, opacity: 0.45 }} />
                                </Box>
                                <Typography sx={{ fontSize: "11.5px", color: T.muted, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{`${r.physical} phys · ${r.remote} rem`}</Typography>
                            </Box>
                        ))}
                        {agg.perPerson.length > 10 && <ShowAllButton expanded={showAllMix} total={agg.perPerson.length} onToggle={() => setShowAllMix((v) => !v)} />}
                        <Box sx={{ display: "flex", gap: "16px", mt: "4px" }}>
                            <Typography sx={{ fontSize: "11px", color: T.muted }}>
                                <Box component="span" sx={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "3px", background: T.purpleDeep, mr: "5px", verticalAlign: "middle" }} />
                                Physical
                            </Typography>
                            <Typography sx={{ fontSize: "11px", color: T.muted }}>
                                <Box component="span" sx={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "3px", background: T.purple, opacity: 0.45, mr: "5px", verticalAlign: "middle" }} />
                                Remote
                            </Typography>
                        </Box>
                    </Box>
                </Panel>

                <Panel title="Visit mix" sub={`${agg.visits} visits in the range`}>
                    {agg.mix.length === 0 ? empty : agg.mix.map(([label, n]) => <BarRow key={label} label={label} value={n} max={agg.mix[0][1]} />)}
                    <Box sx={{ display: "flex", gap: "8px", mt: "12px" }}>
                        <Stat value={agg.onSite} label="On site" />
                        <Stat value={agg.remote} label="Remote" />
                    </Box>
                </Panel>

                <Panel title="Client mix" sub="Industries visited — sets the points rate">
                    {agg.industries.length === 0
                        ? empty
                        : agg.industries.map(([label, n]) => <BarRow key={label} label={label} value={n} max={agg.industries[0][1]} color={label === "not set" ? T.amber : T.purple} />)}
                    {agg.noIndustry > 0 && (
                        <Typography sx={{ fontSize: "11px", color: T.amber, mt: "10px" }}>{agg.noIndustry} visits are on accounts with no industry set.</Typography>
                    )}
                </Panel>

                <Panel title="Status of work in the range">
                    {agg.statuses.length === 0
                        ? empty
                        : agg.statuses.map(([label, n]) => (
                              <BarRow
                                  key={label}
                                  label={label}
                                  value={n}
                                  max={agg.statuses[0][1]}
                                  color={label.toLowerCase() === "completed" ? T.green : label.toLowerCase() === "closed" ? T.muted2 : T.purple}
                              />
                          ))}
                </Panel>
            </Box>
        </Box>
    );
};

export default MetricsTab;
